import React, {useEffect} from 'react';
import {PublisherModel} from "../../models/publisher.model";
import {Grid} from "@material-ui/core";
import styled from "styled-components";
import {Colors} from "../../theme/colors";
import {ExternalLinkTypeEnum} from "../../models/external-link-type.enum";
import {useHistory} from "react-router-dom";
import {StyledButton} from "../ui-base/button";
import emptyLogo from "../../img/empty-publisher-logo.png";
import {DetailsPropWrapper} from "../ui-base/details-prop-wrapper";
import {CustomSmallLinkIcon} from "../ui-base/icons/custom-small-link-icon";
import {CustomEditIcon} from "../ui-base/icons/custom-edit-icon";
import {showSnackSuccess} from "../../store/global/global-slice";
import {useDispatch, useSelector} from "react-redux";
import linkPreview from "../../img/link-preview.png";
import {AuthService} from "../../services/auth.service";
import ShowProfileData from "./show-profile-data";
import {useTranslation} from "react-i18next";
import {BaseService} from "../../services/base.service";
import {CountryModel} from "../../models/country.model";
import {RootState} from "../../store/root-reducer";

export const PublisherDetails: React.FC<{publisher: PublisherModel}> = ({publisher}) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { t } = useTranslation(['publisher', 'common']);
    const [subscription, setSubscription] = React.useState(null);

    const countries: CountryModel[] = useSelector(
        (state: RootState) => state.countries.countries
    );

    useEffect(() => {
        const loadSubscription = async () => {
            const subscriptionLoaded = await AuthService.getSubscription();

            if (subscriptionLoaded && subscriptionLoaded.subscription) {
                setSubscription(t('common:subscriptions.' + subscriptionLoaded.subscription.codename));
            }
        }

        loadSubscription();
    }, [t]);

    const getCountry = (country: string) => {
        if (country) {
            return countries.find((tmpCountry) => tmpCountry.iso === country)?.name || '';
        } else {
            return '';
        }
    }

    const getExternalLinkKey = (name: string) => {
        const linkTypes: string[] = Object.keys(ExternalLinkTypeEnum);

        return linkTypes.find((linkType) => (
            ExternalLinkTypeEnum[linkType] === name
        ));
    }

    const copyLink = () => {
        let temp = document.createElement('textarea');
        temp.value = window.location.origin + '/register?publisher=' + publisher.alias;
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);

        dispatch(showSnackSuccess(t('profile.copiedLink')));
    }

    return (
        <Grid container direction={"column"} spacing={2}>
            <Grid item>
                {subscription &&
                <StyledSubscriptionContainer>
                    <p>{t('profile.yourSubscription')}:</p>
                    <p>{subscription}</p>
                </StyledSubscriptionContainer>
                }

                <div style={{position: 'absolute', top: '1rem', right: '1rem'}}>

                    <StyledEditButton type={"button"} onClick={() => {
                        history.push('/publisher/edit-profile');
                    }}>
                        <CustomEditIcon style={{fontSize: '1.1rem', marginRight: '10px'}}/> {t('profile.editButton')}
                    </StyledEditButton>

                </div>

            </Grid>

            <Grid item container direction={"column"} alignItems={"center"} spacing={1}>
                <Grid item>
                    <StyledAuthorLogo src={publisher.logo?.file || emptyLogo}/>
                </Grid>

                <Grid item>
                    <StyledH6>{publisher.name || '-'}</StyledH6>
                </Grid>
            </Grid>

            <Grid item container direction={"row"} spacing={2}>
                <Grid xs={12} sm={4} md={4} item container direction={"column"} spacing={3}>
                    <DetailsPropWrapper header={t('common:contactPerson') + ':'} text={publisher.details?.contact_person || '-'}/>
                    <DetailsPropWrapper header={t('common:correspondenceAddress') + ':'} text={publisher.details?.address || '-'}/>
                    <DetailsPropWrapper header={t('common:phone') + ':'} text={publisher.details?.phone || '-'}/>
                    <DetailsPropWrapper header={t('common:email') + ':'} text={publisher.details?.contact_email || '-'}/>
                    <DetailsPropWrapper header={t('common:nip') + ':'} text={publisher.details?.nip || '-'}/>
                    <DetailsPropWrapper header={t('common:country') + ':'} text={getCountry(publisher.details?.country) || '-'}/>
                    {/*<DetailsPropCustomValueWrapper header={t('common:socialMedia') + ':'}>*/}
                    {/*    <Grid item container direction={"row"} spacing={2}>*/}
                    {/*        {publisher.external_links.map((externalLink) => (*/}
                    {/*            <Grid item key={externalLink.id}>*/}
                    {/*                <a href={externalLink.url} target="_blank" rel="noreferrer">*/}
                    {/*                    <SocialMediaIcons iconName={getExternalLinkKey(externalLink.name)}/>*/}
                    {/*                </a>*/}
                    {/*            </Grid>*/}
                    {/*        ))}*/}
                    {/*    </Grid>*/}
                    {/*</DetailsPropCustomValueWrapper>*/}
                    <DetailsPropWrapper header={t('profile.description') + ':'} text={BaseService.cutText(publisher.description || '-', 750)}/>
                </Grid>

                {publisher.name &&
                <Grid xs={12} sm={4} md={4} item container direction={"column"} spacing={3}>
                    <StyledPublisherLinkContainer item container direction={"column"} spacing={3}>
                        <Grid item>
                            <StyledPublisherLinkTitle>{t('profile.registerLink')}:</StyledPublisherLinkTitle>
                        </Grid>
                        <Grid item>
                            <StyledPublisherLink>{window.location.origin}/register?publisher={publisher.alias}</StyledPublisherLink>
                        </Grid>
                        <Grid item>
                            <StyledButton type={"button"} color={"transparentViolet"} onClick={() => {
                                copyLink();
                            }}>
                                <CustomSmallLinkIcon style={{fontSize: '1.1rem', marginRight: '10px'}}/> {t('profile.copyLink')}
                            </StyledButton>
                        </Grid>
                        <Grid item>
                            <p>{t('profile.registerLinkDesc')}</p>
                        </Grid>
                        <Grid item container justifyContent={"center"}>
                            <img src={linkPreview} style={{marginBottom: '-24px'}} alt={'Link preview'}/>
                        </Grid>
                    </StyledPublisherLinkContainer>
                </Grid>
                }

                <Grid xs={12} sm={publisher.name ? 4 : 8} md={publisher.name ? 4 : 8} item container direction={"column"} spacing={3}>
                    <Grid item>
                        <StyledPublisherLinkTitle>{t('profile.previewProfile')}:</StyledPublisherLinkTitle>
                    </Grid>

                    <ShowProfileData publisherId={publisher.id}/>
                </Grid>

            </Grid>

        </Grid>
    );
}

const StyledAuthorLogo = styled('img')`
  height: 100px;
`;

const StyledSubscriptionContainer = styled('div')`
  position: absolute;
  top: 1rem;
  left: 1rem;
  
  p {
    font-weight: 600;
    
    &:first-child {
      font-weight: 500;
      color: #451356;
    }
    
    &:last-child {
      font-size: 1.1rem;
      color: #6714A3;
    }
  }
`;

const StyledEditButton = styled(StyledButton)`
  position: absolute;
  top: 0;
  right: 0;
`;

const StyledH6 = styled('h6')`
  font-size: 1.5rem;
  color: ${Colors.violet};
  font-weight: 600;
`;

const StyledPublisherLinkContainer = styled(Grid)`
  background: #FBF5FF;
  border: 1px solid #6714A3;
  box-sizing: border-box;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  border-radius: 4px;
`;

const StyledPublisherLinkTitle = styled('p')`
  font-size: 1rem;
  font-weight: 600;
  color: ${Colors.lightViolet};
`;

const StyledPublisherLink = styled('p')`
  font-size: 0.9rem;
  font-weight: 500;
  color: #0B5CD7;
  background-color: #fff;
  padding: 0.5rem 2rem;
`;
